import * as React from "react";
import ReactMarkdown from "react-markdown";
import { formatTimestamp } from "@/lib/utils";
import { Loader } from "lucide-react";
const MessageActions = React.lazy(() => import("./MessageActions"));
const RelatedQuestions = React.lazy(() => import("./RelatedQuestions"));
import { ChatMessage } from "../../types/discuss.types"; // Assuming a common type or adjust as needed
import { ResearchChatMessage } from "@/types/research.types";
import { useIsMutating } from "@tanstack/react-query";

interface MessageProps {
  type: "user" | "assistant";
  message: string;
  creation_date: string;
  messageObject?: ChatMessage | ResearchChatMessage; // The full message object for actions
  relatedQuestions?: string[];
  onRelatedQuestionClick?: (question: string) => void;
  requestType?: "discuss" | "research";
  documentId?: string;
  documentTitle?: string;
  prevMessage?: ChatMessage | ResearchChatMessage;
  bulkActions?: React.ReactNode;
}

const markdownComponents = {
  a: (props: React.JSX.IntrinsicElements["a"] & { node?: unknown }) => {
    const { node: _node, ...rest } = props;
    return <a {...rest} target="_blank" rel="noopener noreferrer" />;
  },
  pre: (props: React.JSX.IntrinsicElements["pre"] & { node?: unknown }) => {
    const { node: _node, ...rest } = props;
    return (
      <div className="overflow-x-auto w-full max-w-full my-2 rounded-md">
        <pre {...rest} />
      </div>
    );
  },
};

const UserMessageContent: React.FC<{ message: string; creation_date: string }> = ({
  message,
  creation_date,
}) => (
  <React.Fragment>
    <ReactMarkdown components={markdownComponents}>{message}</ReactMarkdown>
    <div className="text-xs text-neutral-500 mt-1 self-end">{formatTimestamp(creation_date)}</div>
  </React.Fragment>
);

interface AssistantMessageContentProps {
  message: string;
  creation_date: string;
  messageObject?: ChatMessage | ResearchChatMessage;
  requestType?: "discuss" | "research";
  documentId?: string;
  documentTitle?: string;
  prevMessage?: ChatMessage | ResearchChatMessage;
}

const AssistantMessageContent: React.FC<AssistantMessageContentProps> = ({
  message,
  creation_date,
  messageObject,
  requestType,
  documentId,
  documentTitle,
  prevMessage,
}) => (
  <React.Fragment>
    <div className="flex gap-3">
      <div
        style={{ wordBreak: "break-word" }}
        className="flex-1 min-w-0 prose prose-sm prose-li:marker:text-neutral-200 max-w-none prose-a:text-primary-300 prose-hr:my-2"
      >
        <ReactMarkdown components={markdownComponents}>{message}</ReactMarkdown>
      </div>
      {messageObject && (
        <div className="sticky bottom-3 self-end">
          <React.Suspense fallback={<Loader className="animate-spin" />}>
            <MessageActions
              message={messageObject}
              source={requestType ?? "discuss"}
              messageText={message}
              documentId={documentId}
              documentTitle={documentTitle}
              prevMessage={prevMessage}
            />
          </React.Suspense>
        </div>
      )}
    </div>
    <div className="text-xs text-neutral-500 mt-2 self-start">{formatTimestamp(creation_date)}</div>
  </React.Fragment>
);

const Message = React.forwardRef<HTMLDivElement, MessageProps>(
  (
    {
      type,
      message,
      creation_date,
      messageObject,
      relatedQuestions,
      onRelatedQuestionClick,
      requestType,
      documentId,
      documentTitle,
      prevMessage,
      bulkActions,
    },
    ref
  ) => {
    const isUser = type === "user";
    const isSendingDiscuss = useIsMutating({ mutationKey: ["sendChatMessage"] });
    const isSendingResearch = useIsMutating({ mutationKey: ["sendResearchMessage"] });
    const isSendingMessage = isSendingDiscuss > 0 || isSendingResearch > 0;

    return (
      <div ref={ref} className={`flex flex-col w-full ${isUser ? "items-end" : "items-start"}`}>
        <div
          className={`flex flex-row gap-2 max-w-full ${isUser ? "justify-end" : "justify-start"}`}
        >
          <div
            className={`px-4 py-3 rounded-lg text-sm min-w-[150px] max-w-11/12 flex flex-col gap-2 ${isUser ? "bg-primary-100 text-neutral-400" : "bg-neutral-700 text-neutral-200"}`}
          >
            {isUser ? (
              <UserMessageContent message={message} creation_date={creation_date} />
            ) : (
              <AssistantMessageContent
                message={message}
                creation_date={creation_date}
                messageObject={messageObject}
                requestType={requestType}
                documentId={documentId}
                documentTitle={documentTitle}
                prevMessage={prevMessage}
              />
            )}
          </div>
          {!isUser && bulkActions != null && (
            <div className="sticky bottom-3 self-end mb-2 flex-shrink-0">{bulkActions}</div>
          )}
        </div>
        {!isUser &&
          relatedQuestions &&
          relatedQuestions.length > 0 &&
          onRelatedQuestionClick &&
          !isSendingMessage && (
            <div className="mt-5 w-full">
              <React.Suspense fallback={<Loader className="animate-spin" />}>
                <RelatedQuestions
                  questions={relatedQuestions}
                  onQuestionClick={onRelatedQuestionClick}
                  requestType={requestType ?? "discuss"}
                />
              </React.Suspense>
            </div>
          )}
      </div>
    );
  }
);

Message.displayName = "Message";

export default Message;
